import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from './toast.service';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { DiplomaLanguageModalComponent } from 'app/main/components/diploma-language-modal/diploma-language-modal.component';
import { ErrorService } from '../services/error.service';

@Injectable({
	providedIn: 'root',
})
export class DialogService {
	constructor(
		private _toastService: ToastService,
		private translate: TranslateService,
		private _errorService: ErrorService,
		private _modalService: NgbModal
	) {}

	showToast(type: 'show' | 'success' | 'error' | 'info' | 'warning', title: string, message: string, closeButton = true) {
		if (type === 'error' && this._errorService.shouldSuppressGenericErrorToast()) {
			return;
		}

		// un solo messaggio alla volta, come prima con ngx-toastr
		this.clearToast();

		this._toastService.show({
			type,
			title: title ? this.translate.instant(title) : undefined,
			message: message ? this.translate.instant(message) : '',
			closable: closeButton,
		});
	}

	clearToast() {
		this._toastService.clear();
	}

	fireSwal(
		title = 'common_swal_confirm_deletion_title',
		text = 'common_swal_operation_cannot_be_undone_text',
		icon: SweetAlertIcon = 'warning',
		okText = 'common_toast_confirm',
		cancelText = 'common_btn_cancel'
	) {
		return Swal.fire({
			title: this.translate.instant(title),
			html: this.translate.instant(text),
			icon: icon,
			showCancelButton: true,
			confirmButtonColor: '#7367F0',
			cancelButtonColor: '#E42728',
			cancelButtonText: this.translate.instant(cancelText),
			confirmButtonText: this.translate.instant(okText),
			customClass: {
				confirmButton: 'btn btn-primary',
				cancelButton: 'btn btn-danger ms-1',
			},
		});
	}

	fireSwalCestino(title = 'common_swal_confirm_deletion_title', text = 'common_swal_file_moved_to_trash_text', icon: SweetAlertIcon = 'warning') {
		return Swal.fire({
			title: this.translate.instant(title),
			text: this.translate.instant(text),
			icon: icon,
			showCancelButton: true,
			confirmButtonColor: '#7367F0',
			cancelButtonColor: '#E42728',
			cancelButtonText: this.translate.instant('common_btn_cancel'),
			confirmButtonText: this.translate.instant('common_toast_confirm'),
			customClass: {
				confirmButton: 'btn btn-primary',
				cancelButton: 'btn btn-danger ms-1',
			},
		});
	}

	fireSwalRestore(title = 'common_swal_confirm_restore_title', text = 'common_swal_item_will_be_restored_text', icon: SweetAlertIcon = 'warning') {
		return Swal.fire({
			title: this.translate.instant(title),
			text: this.translate.instant(text),
			icon: icon,
			showCancelButton: true,
			confirmButtonColor: '#7367F0',
			cancelButtonColor: '#E42728',
			cancelButtonText: this.translate.instant('common_btn_cancel'),
			confirmButtonText: this.translate.instant('common_toast_confirm'),
			customClass: {
				confirmButton: 'btn btn-primary',
				cancelButton: 'btn btn-danger ms-1',
			},
		});
	}

	fireSwalToggleActive() {
		return Swal.fire({
			title: this.translate.instant('common_swal_are_you_sure_title'),
			text: this.translate.instant('common_swal_toggle_active_text'),
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: this.translate.instant('common_btn_yes_change'),
			cancelButtonText: this.translate.instant('common_btn_cancel'),
		});
	}

	fireSwalWithInput(title: string, text: string, inputPlaceholder: string, inputValue: string = '') {
		return Swal.fire({
			title,
			text,
			input: 'text',
			inputPlaceholder,
			inputValue,
			showCancelButton: true,
			confirmButtonColor: '#7367F0',
			cancelButtonColor: '#E42728',
			cancelButtonText: this.translate.instant('common_btn_cancel'),
			confirmButtonText: this.translate.instant('common_btn_confirm'),
			customClass: {
				confirmButton: 'btn btn-primary',
				cancelButton: 'btn btn-danger ms-1',
			},
			inputValidator: value => {
				if (!value || value.trim().length === 0) {
					return this.translate.instant('common_validation_name_cannot_be_empty');
				}
				return null;
			},
		});
	}

	fireSwalLanguageSelect(title = 'common_swal_select_document_language_title'): Promise<string | null> {
		const ref = this._modalService.open(DiplomaLanguageModalComponent, { size: 'sm', centered: true });
		ref.componentInstance.title = title;
		return ref.result.then(
			(lang: string) => lang,
			() => null
		);
	}
}
